import { useCallback, useState } from 'react';

type SortOrder = 'asc' | 'desc';

function useTableState<TFilters extends Record<string, any>>(
  initialSortBy: string,
  initialSortOrder: SortOrder = 'asc',
  defaultFilters: TFilters,
) {
  const [page, setPage] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [filters, setFilters] = useState<TFilters>(defaultFilters);

  const handleFilterChange = useCallback(
    (name: keyof TFilters, value: TFilters[keyof TFilters]) => {
      setPage(0);
      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleFiltersReplace = useCallback((newFilters: TFilters) => {
    setPage(0);
    setFilters(newFilters);
  }, []);

  const handlePageChange = useCallback((value: number) => setPage(value), []);

  const handleSortChange = useCallback(
    (sortField: string) => {
      if (sortField === sortBy) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(sortField);
        setSortOrder(initialSortOrder);
      }
    },
    [initialSortOrder, sortBy],
  );

  const clearTableState = useCallback(() => {
    setPage(0);
    setFilters(defaultFilters);
  }, [defaultFilters]);

  return {
    page,
    sortBy,
    sortOrder,
    filters,
    handleFilterChange,
    handleFiltersReplace,
    handlePageChange,
    handleSortChange,
    clearTableState,
  };
}

export default useTableState;
